import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { uploadAPI } from '../../services/api';

const UploadSection = () => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [embeddingProgress, setEmbeddingProgress] = useState(false);
  const queryClient = useQueryClient();

  // Get upload status
  const { data: uploadStatus, isLoading } = useQuery({
    queryKey: ['uploadStatus'],
    queryFn: () => uploadAPI.getUploadStatus(),
    retry: 1,
    refetchInterval: embeddingProgress ? 2000 : false, // Poll every 2 seconds if generating embeddings
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (file) => uploadAPI.uploadConnections(file, setUploadProgress),
    onSuccess: (response) => {
      toast.success('File uploaded successfully! Processing your connections...');
      queryClient.invalidateQueries(['uploadStatus']);
      setUploadProgress(0);
      // Start data processing
      setEmbeddingProgress(true);
      embeddingMutation.mutate();
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Upload failed';
      toast.error(message);
      setUploadProgress(0);
    },
  });

  // Data processing mutation
  const embeddingMutation = useMutation({
    mutationFn: () => uploadAPI.generateEmbeddings(),
    onSuccess: (response) => {
      toast.success('Processing complete! Your data is ready for search.');
      setEmbeddingProgress(false);
      queryClient.invalidateQueries(['uploadStatus']);
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Data processing failed';
      toast.error(message);
      setEmbeddingProgress(false);
    },
  });

  // Delete connections mutation
  const deleteMutation = useMutation({
    mutationFn: (deleteFiles) => uploadAPI.deleteConnections(deleteFiles),
    onSuccess: () => {
      toast.success('Connections deleted successfully');
      queryClient.invalidateQueries(['uploadStatus']);
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Delete failed';
      toast.error(message);
    },
  });

  // File drop handler
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file');
        return;
      }
      if (file.size > 16 * 1024 * 1024) { // 16MB
        toast.error('File size must be less than 16MB');
        return;
      }
      uploadMutation.mutate(file);
    }
  }, [uploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete all your connections?')) {
      deleteMutation.mutate(false);
    }
  };

  const status = uploadStatus?.data;
  const hasConnections = status?.has_connections;
  const connectionCount = status?.connection_count || 0;
  const embeddingStats = status?.embedding_stats;

  // Check if embeddings are still being generated based on status
  React.useEffect(() => {
    if (embeddingStats && embeddingStats.embedding_status === 'complete') {
      setEmbeddingProgress(false);
    }
  }, [embeddingStats]);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Upload LinkedIn Connections
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Upload your LinkedIn connections CSV file to start searching and analyzing your network.
      </Typography>

      {/* Current Status */}
      {!isLoading && hasConnections && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">
            ✅ You have {connectionCount.toLocaleString()} connections uploaded
          </Typography>
          {embeddingStats && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              🔄 Processing: {embeddingStats.embeddings_generated.toLocaleString()} / {embeddingStats.total_connections.toLocaleString()} connections processed ({embeddingStats.embedding_percentage}%)
              {embeddingStats.embedding_status === 'pending' && ' - Processing in background...'}
              {embeddingStats.embedding_status === 'partial' && ' - Partial completion'}
              {embeddingStats.embedding_status === 'complete' && ' - Complete ✅'}
            </Typography>
          )}
        </Alert>
      )}

      {/* Processing Progress */}
      {(embeddingProgress || embeddingMutation.isPending) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">
            🔄 Processing your connections for search functionality...
          </Typography>
          <LinearProgress sx={{ mt: 1 }} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            This process may take a few minutes depending on the number of connections.
          </Typography>
        </Alert>
      )}

      {/* Upload Area */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            {...getRootProps()}
            sx={{
              border: 2,
              borderStyle: 'dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'primary.50' : 'background.paper',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'primary.50',
              },
            }}
          >
            <input {...getInputProps()} />
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop your CSV file here' : 'Upload LinkedIn Connections CSV'}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Drag and drop your file here, or click to browse
            </Typography>
            
            <Button
              variant="contained"
              disabled={uploadMutation.isPending}
              sx={{ mt: 1 }}
            >
              Choose File
            </Button>
          </Box>

          {/* Upload Progress */}
          {uploadMutation.isPending && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Uploading and processing... {uploadProgress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📋 How to Get Your LinkedIn Data
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <Chip label="1" size="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Go to LinkedIn Settings"
                secondary={
                  <>
                    LinkedIn → Settings & Privacy → Data Privacy or visit{' '}
                    <a
                      href="https://www.linkedin.com/mypreferences/d/download-my-data"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'none' }}
                    >
                      this direct link
                    </a>
                  </>
                }
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Chip label="2" size="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Request Your Data"
                secondary="Click 'Get a copy of your data' → Select 'Connections'"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Chip label="3" size="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Download & Upload"
                secondary="Download the CSV file and upload it here"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Recent Connections Preview */}
      {hasConnections && status?.recent_connections && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📊 Some Connections
            </Typography>
            
            <List dense>
              {status.recent_connections.slice(0, 5).map((conn, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <FileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${conn.first_name} ${conn.last_name}`}
                    secondary={`${conn.position || 'No position'} at ${conn.company || 'No company'}`}
                  />
                </ListItem>
              ))}
            </List>
            
            {status.recent_connections.length > 5 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                ... and {connectionCount - 5} more connections
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

    </Box>
  );
};

export default UploadSection;